import React from 'react';
import styled from 'styled-components';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { hexToRgba } from '../../utils/colorUtils';

export type FormAlertVariant = 'error' | 'info' | 'warning';

interface FormAlertProps {
  variant?: FormAlertVariant;
  children: React.ReactNode;
  className?: string;
  id?: string;
}

const AlertRoot = styled.div<{ $variant: FormAlertVariant }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  padding: ${spacing[4]} ${spacing[5]};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid
    ${props => {
      const { colors } = props.theme;
      if (props.$variant === 'error') return hexToRgba(colors.error, 0.35);
      if (props.$variant === 'warning') return hexToRgba(colors.warning, 0.35);
      return hexToRgba(colors.icomBlue || colors.info, 0.3);
    }};
  border-left-width: 4px;
  border-left-color: ${props => {
    const { colors } = props.theme;
    if (props.$variant === 'error') return colors.error;
    if (props.$variant === 'warning') return colors.warning;
    return colors.icomBlue || colors.info;
  }};
  background: ${props => {
    const { colors } = props.theme;
    if (props.$variant === 'error') return colors.errorBg || hexToRgba(colors.error, 0.08);
    if (props.$variant === 'warning') return colors.warningBg || hexToRgba(colors.warning, 0.1);
    return colors.icomLightBlue || hexToRgba(colors.info, 0.08);
  }};
  color: ${props => {
    const { colors } = props.theme;
    if (props.$variant === 'error') return colors.error;
    if (props.$variant === 'warning') return colors.warning;
    return colors.textSecondary;
  }};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.relaxed};
  margin-bottom: ${spacing[6]};
`;

/**
 * Accessible form alert without emoji prefixes.
 * Shared by registration wizards (individual / institution).
 */
export const FormAlert: React.FC<FormAlertProps> = ({
  variant = 'info',
  children,
  className,
  id,
}) => (
  <AlertRoot
    id={id}
    className={className}
    $variant={variant}
    role={variant === 'error' ? 'alert' : 'status'}
    aria-live={variant === 'error' ? 'assertive' : 'polite'}
  >
    {children}
  </AlertRoot>
);

export default FormAlert;
